import {computed, Ref} from "vue";
import {useNow} from "@vueuse/core";
import usePlanningRange from "@/planning/composables/usePlanningRange";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {AgendaPresenceLane, buildPresenceLanes} from "@/agenda/lib/presence";

/**
 * Team presence of the agenda's displayed day, resolved with the exact same
 * rules as the Planning screen (recurrence parity, dated overrides, approved
 * absences) — the two surfaces read the same data and cannot disagree.
 *
 * `enabled` gates the fetching: the agenda mounts this in every view, and a
 * day the rail never draws must not cost two requests per navigation.
 */
const useAgendaPresence = (day: Ref<Date>, enabled: Ref<boolean>) => {
    const dayStart = computed(() => parseAgendaDate(day.value).startOf('day'));
    const {members, resolveDay, loading, failed} = usePlanningRange(dayStart, dayStart, enabled);
    const now = useNow({interval: 60 * 1000});

    const presenceLanes = computed<AgendaPresenceLane[]>(() => buildPresenceLanes(
        members.value.map(member => resolveDay(member, dayStart.value)),
        now.value
    ));

    /**
     * A failed range keeps the previous period's data, so the rail would show
     * a member on approved leave as present indefinitely: it is dropped
     * entirely. That costs one layout shift, accepted because a failure is
     * exceptional — unlike a day change — and because a dimmed empty rail
     * left forever, with no error surface in the agenda, says less than none.
     */
    const presenceUsable = computed<boolean>(() => enabled.value && !failed.value);

    /**
     * Absences arrive after the recurrence resolves, so a refresh in progress
     * is not yet trustworthy — but unmounting the rail for it collapsed the
     * hour axis and reflowed the whole grid on every day change. The rail
     * stays, dimmed, and stops asserting who is present right now.
     */
    const presencePending = computed<boolean>(() => enabled.value && loading.value);

    return {presenceLanes, presenceUsable, presencePending};
};

export default useAgendaPresence;
